import React from 'react';
import { 
  CheckCircle, 
  Users, 
  Shield, 
  Clock, 
  Award, 
  MapPin, 
  Wrench,
  Phone,
  Star,
  Zap,
  TrendingUp,
  Target,
  Heart
} from 'lucide-react';

const AboutUs = () => {
  const journey = [
    { year: '2020', title: 'Founded', desc: 'Started with a vision to revolutionize bike servicing' },
    { year: '2022', title: 'Expansion', desc: 'Grew to 25+ service centers across Pune' },
    { year: '2024', title: 'Excellence', desc: 'Served 5000+ customers with 4.8★ rating' },
    { year: '2026', title: 'Innovation', desc: 'Launched AI-powered service recommendations' }
  ];

  const services = [
    {
      title: 'Basic Service',
      price: '₹499',
      features: ['Oil Change', 'Air Filter Check', 'Basic Inspection', 'Chain Lubrication'],
      icon: '🔧',
      popular: false
    },
    {
      title: 'Premium Service',
      price: '₹999',
      features: ['Complete Tune-up', 'Brake Service', 'Spark Plug Change', 'All Fluids Top-up'],
      icon: '⭐',
      popular: true
    },
    {
      title: 'Complete Care',
      price: '₹1499',
      features: ['Full Service', 'Engine Cleaning', 'Suspension Check', '6-Month Warranty'],
      icon: '💎',
      popular: false
    }
  ];

  const whyUs = [
    { icon: <Shield className="w-8 h-8" />, title: '100% Genuine Parts', desc: 'Only authentic spares used', color: 'blue' },
    { icon: <Users className="w-8 h-8" />, title: 'Expert Mechanics', desc: 'Certified & experienced', color: 'green' },
    { icon: <Clock className="w-8 h-8" />, title: 'Quick Service', desc: 'Same-day delivery', color: 'orange' },
    { icon: <Award className="w-8 h-8" />, title: 'Quality Assured', desc: 'Warranty on services', color: 'purple' },
    { icon: <MapPin className="w-8 h-8" />, title: 'Free Pickup', desc: 'Doorstep convenience', color: 'red' },
    { icon: <Zap className="w-8 h-8" />, title: 'Transparent Pricing', desc: 'No hidden charges', color: 'yellow' }
  ];

  const testimonials = [
    { name: 'Rahul Sharma', role: 'Software Engineer', text: 'Best bike service in Pune! Quick, reliable, and affordable.', rating: 5 },
    { name: 'Priya Patil', role: 'Designer', text: 'Loved the doorstep service. Very professional team.', rating: 5 },
    { name: 'Amit Kumar', role: 'Entrepreneur', text: 'Transparent pricing and genuine parts. Highly recommend!', rating: 5 }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Split Design */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 min-h-[600px]">
            {/* Left Side - Content */}
            <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white p-8 sm:p-12 lg:p-16 flex items-center">
              <div>
                <div className="inline-block px-4 py-2 bg-white/20 rounded-full text-sm font-semibold mb-6">
                  🚀 Pune's #1 Bike Service Platform
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                  We Keep Your Bike Running Smooth
                </h1>
                <p className="text-lg sm:text-xl text-blue-100 mb-8 leading-relaxed">
                  Professional bike servicing at your doorstep. Expert care, genuine parts, and unbeatable prices.
                </p>
                <div className="flex flex-wrap gap-4">
                  <button 
                    onClick={() => window.location.href = '/'}
                    className="px-8 py-4 bg-white text-blue-600 rounded-xl font-bold hover:bg-gray-100 transition-all shadow-lg"
                  >
                    Book Service Now
                  </button>
                
                </div>
              </div>
            </div>

            {/* Right Side - Visual */}
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 p-8 sm:p-12 flex items-center justify-center">
              <div className="text-center">
                <div className="text-9xl mb-6">🏍️</div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded-xl shadow-md">
                    <div className="text-2xl font-bold text-blue-600">5K+</div>
                    <div className="text-xs text-gray-600">Customers</div>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-md">
                    <div className="text-2xl font-bold text-green-600">4.8★</div>
                    <div className="text-xs text-gray-600">Rating</div>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-md">
                    <div className="text-2xl font-bold text-orange-600">25+</div>
                    <div className="text-xs text-gray-600">Centers</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      

      {/* Mission & Vision - Card Layout */}
      <div className="py-16 sm:py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Mission */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-3xl border-2 border-blue-200">
              <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
              <p className="text-gray-700 leading-relaxed">
                To make bike servicing hassle-free, transparent, and accessible. We're building trust through quality service, genuine parts, and fair pricing.
              </p>
            </div>

            {/* Vision */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-8 rounded-3xl border-2 border-green-200">
              <div className="w-16 h-16 bg-green-600 rounded-2xl flex items-center justify-center mb-6">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
              <p className="text-gray-700 leading-relaxed">
                To become India's most trusted bike service platform, expanding to every city and making professional care accessible to all riders.
              </p>
            </div>

            {/* Values */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-8 rounded-3xl border-2 border-purple-200">
              <div className="w-16 h-16 bg-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Values</h3>
              <p className="text-gray-700 leading-relaxed">
                Transparency, Quality, Reliability, and Customer Satisfaction. We treat every bike as our own and every customer as family.
              </p>
            </div>
          </div>
        </div>
      </div>

      

      {/* Why Choose Us - Icon Grid */}
      <div className="py-16 sm:py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Why Choose OkMechanic?</h2>
            <p className="text-lg text-gray-600">Six reasons why we're Pune's favorite</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyUs.map((item, index) => (
              <div 
                key={index} 
                className="group bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border-2 border-gray-200 hover:border-blue-600 hover:shadow-xl transition-all"
              >
                <div className={`w-16 h-16 bg-${item.color}-100 rounded-2xl flex items-center justify-center mb-6 text-${item.color}-600 group-hover:scale-110 transition-transform`}>
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Testimonials - Card Slider */}
      <div className="py-16 sm:py-24 px-4 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">What Customers Say</h2>
            <p className="text-lg text-gray-600">Real reviews from real riders</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((test, index) => (
              <div key={index} className="bg-white p-8 rounded-3xl shadow-lg">
                <div className="flex mb-4">
                  {[...Array(test.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed">"{test.text}"</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                    {test.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">{test.name}</div>
                    <div className="text-sm text-gray-600">{test.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Service Areas - Map Style */}
      <div className="py-16 sm:py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <MapPin className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">We Serve Across Pune</h2>
            <p className="text-lg text-gray-600">Free pickup & delivery in these areas</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[
              'Kothrud', 'Baner', 'Hinjewadi', 'Kharadi', 'Viman Nagar', 'Hadapsar',
              'Wakad', 'Aundh', 'Shivajinagar', 'Koregaon Park', 'Pimpri', 'Chinchwad'
            ].map((area, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl text-center font-semibold text-gray-800 hover:from-blue-600 hover:to-blue-700 hover:text-white transition-all cursor-pointer shadow-md hover:shadow-lg"
              >
                {area}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section - Full Width Banner */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-purple-600 text-white py-16 sm:py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Wrench className="w-20 h-20 mx-auto mb-6 opacity-90" />
          <h2 className="text-3xl sm:text-5xl font-bold mb-6">
            Your Bike Deserves the Best
          </h2>
          <p className="text-xl text-blue-100 mb-10 leading-relaxed">
            Join 5000+ happy customers who trust OkMechanic for their bike servicing needs
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <button 
              onClick={() => window.location.href = '/'}
              className="px-10 py-5 bg-white text-blue-600 rounded-2xl font-bold text-lg hover:bg-gray-100 transition-all shadow-2xl"
            >
              Book Service Now
            </button>
            <button 
              onClick={() => window.location.href = 'tel:+919112412191'}
              className="px-10 py-5 bg-white/10 backdrop-blur-sm text-white rounded-2xl font-bold text-lg hover:bg-white/20 transition-all border-2 border-white"
            >
              <Phone className="w-6 h-6 inline mr-2" />
              Call: +91 9112412191
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-blue-100">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              <span>Free Doorstep Service</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              <span>Genuine Parts Only</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              <span>6-Month Warranty</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;
